import React from "react"
import Paper from "../../ui/Paper"
import Input from "../../ui/Input"
import { useDispatch, useSelector } from "react-redux"
import { addPriorityList, selectCreatingPriorityList } from "./slice"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import styled from "styled-components"
import Label from "../../ui/Label"
import FormError from "../../ui/FormError"
import TextArea from "../../ui/TextArea"
import FormGroup from "../../ui/FormGroup"
import Button from "../../ui/Button"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import ErrorMsg from "../../ui/ErrorMsg"
import PropTypes from "prop-types"
import { slideFromRight } from "../../ui/animations"
import { selectAstrositesById } from "../astrosites/slice"
import Tip from "../../ui/Tip"

const Panel = styled(Paper)`
  padding: 2rem;
  overflow-y: auto;
  width: 25vw;
  min-width: 20rem;
  max-width: 40rem;
  animation: 0.3s ${slideFromRight} ease-out;
`
const FormContainer = styled.div``

/**
 * The form validator
 */
const schema = yup
  .object({
    name: yup.string().max(30).required(),
    description: yup.string().max(150),
    priority: yup.number().min(0).integer().required(),
    selectedAstrosites: yup.array(),
    norad_ids: yup
      // FIXME: Replace with "must contain at least one NORAD ID - see rsos/parsing.js
      .string()
      .test(
        "list-validation",
        "norad_ids must be at least one id in a comma seperated list of integers",
        (value) => {
          if (value === "") {
            return false // Empty string is valid
          }
          const pattern = /^(\d+,)*\d+$/
          return pattern.test(value)
        }
      ),
  })
  .required()

/**
 * Create a new priority list.
 * Priority lists are use to group and manage objects and their observation priority.
 * A higher priority means we care more about this list of objects.
 *
 * @param handleCancel
 * @returns {JSX.Element}
 * @constructor
 */
const CreatePriorityList = ({ handleCancel }) => {
  const dispatch = useDispatch()
  const inProgressList = useSelector((state) =>
    selectCreatingPriorityList(state)
  )

  const astrositeIDs = Object.keys(useSelector(selectAstrositesById))

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      selectedAstrosites: [],
    },
    resolver: yupResolver(schema),
  })
  const onSubmit = (data) => {
    dispatch(addPriorityList(data))
      .unwrap()
      .then(() => {
        handleCancel()
      })
  }

  return (
    <Panel>
      <h2>New priority list</h2>
      {inProgressList?._saveStatus === "creating" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}
      {inProgressList?._saveStatus == "rejected" && (
        <ErrorMsg
          msg={
            inProgressList._errorMessage ||
            "an error occurred creating the priority list"
          }
          showIcon={true}
        />
      )}
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="name">Name:</Label>
            <Input type="text" {...register("name")} autoFocus />
            <FormError>{errors.name?.message}</FormError>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="description">Description:</Label>
            <TextArea {...register("description")} rows="4" />
            <FormError>{errors.description?.message}</FormError>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="priority">Priority:</Label>
            <Input type="number" defaultValue={0} {...register("priority")} />
            <Tip>Higher receives priority when scheduling</Tip>
            <FormError>{errors.priority?.message}</FormError>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="selectedAstrosites">Active for:</Label>
            {astrositeIDs?.map((astrosite) => (
              <Controller
                key={astrosite}
                name="selectedAstrosites"
                control={control}
                render={({ field: { onChange, onBlur, value, ref } }) => {
                  return (
                    <label>
                      <input
                        type="checkbox"
                        ref={ref}
                        onBlur={onBlur}
                        onChange={(e) => {
                          const isChecked = e.target.checked
                          const newValue = isChecked
                            ? [...value, astrosite] // Add item if checked
                            : value?.filter((i) => i !== astrosite) // Remove item if unchecked
                          onChange(newValue) // Update the form state
                        }}
                        checked={value?.includes(astrosite)}
                      />
                      {astrosite}
                    </label>
                  )
                }}
              />
            ))}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="norad_ids">RSOs in list:</Label>
            <TextArea {...register("norad_ids")} rows="10" />
            <Tip>Comma-separated list of NORAD IDs</Tip>
            <FormError>{errors.norad_ids?.message}</FormError>
          </FormGroup>
          <Button type="submit">submit</Button>
          {handleCancel && <Button onClick={handleCancel}>close</Button>}
        </form>
      </FormContainer>
    </Panel>
  )
}

CreatePriorityList.propTypes = {
  handleCancel: PropTypes.func, // Cancel the creation of a priority list
}
export default CreatePriorityList
